import React from "react";
import { LuLoader, LuCircleCheck, LuX } from "react-icons/lu";
import {
  FileText,
  BookOpenText,
  FileArchive,
  Plus,
  FileType,
} from "lucide-react";
import { twMerge } from "tailwind-merge";

// Types
type IFileItemData = {
  id: string;
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  progress?: number;
  errorMessage?: string;
};

type IFileItem = {
  fileItem: IFileItemData;
  deleteLoading?: boolean;
  onRemove: (fileId: string) => void;
};

const FileItem: React.FC<IFileItem> = ({
  fileItem,
  onRemove,
  deleteLoading,
}) => {
  const getFileIcon = (file: File) => {
    const extension = file.name.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "pdf":
        return <FileText size={28} className="text-red-500" />;
      case "doc":
      case "docx":
        return <BookOpenText size={28} className="text-blue-500" />;
      case "xls":
      case "xlsx":
        return <FileArchive size={28} className="text-green-500" />;
      case "ppt":
      case "pptx":
        return <Plus size={28} className="text-orange-500" />;
      case "txt":
        return <FileType size={28} className="text-gray-500" />;
      default:
        return <FileText size={28} className="text-gray-400" />;
    }
  };

  const getFileTypeLabel = (file: File): string => {
    const extension = file.name.split(".").pop()?.toUpperCase() || "";
    switch (extension) {
      case "PDF":
        return "PDF Document";
      case "DOC":
        return "Word Document";
      case "DOCX":
        return "Word Document";
      case "XLS":
        return "Excel Spreadsheet";
      case "XLSX":
        return "Excel Spreadsheet";
      case "PPT":
        return "PowerPoint";
      case "PPTX":
        return "PowerPoint";
      case "TXT":
        return "Text File";
      default:
        return extension || "Document";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="relative bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-all duration-200">
      <div className="flex items-start gap-3">
        {/* Document Icon */}
        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          {getFileIcon(fileItem.file)}
        </div>

        {/* Document Info - takes remaining space */}
        <div className="flex-1 min-w-0 pr-8">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-medium text-gray-900 truncate mb-0 pr-4"
                title={fileItem.file.name}
              >
                {fileItem.file.name}
              </p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-gray-500">
                  {getFileTypeLabel(fileItem.file)}
                </span>
                <span className="text-xs text-gray-400">•</span>
                <span className="text-xs text-gray-500">
                  {formatFileSize(fileItem.file.size)}
                </span>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {fileItem.status === "uploading" && (
            <div className="mt-2">
              <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-gray-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${fileItem.progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Uploading... {fileItem.progress}%
              </p>
            </div>
          )}

          {/* Status Indicator */}
          {fileItem.status === "success" && (
            <div className="flex items-center gap-1 mt-1">
              <LuCircleCheck size={14} className="text-green-500" />
              <span className="text-xs text-green-600">
                Uploaded successfully
              </span>
            </div>
          )}

          {/* Error Status */}
          {fileItem.status === "error" && fileItem.errorMessage && (
            <div className="flex items-center gap-1 mt-1">
              <span className="text-xs text-red-600">
                {fileItem.errorMessage}
              </span>
            </div>
          )}
        </div>

        {/* Loading Spinner */}
        {fileItem.status === "uploading" && (
          <div className="flex-shrink-0">
            <LuLoader size={20} className="animate-spin text-gray-500" />
          </div>
        )}
      </div>

      {/* Delete Button - positioned absolutely */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(fileItem.id);
        }}
        className={twMerge(
          "absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors z-10",
          fileItem.status === "uploading" ? "hidden" : "cursor-pointer",
        )}
        disabled={fileItem.status === "uploading"}
      >
        {deleteLoading ? (
          <LuLoader size={18} className="animate-spin" />
        ) : (
          <LuX size={18} />
        )}
      </button>
    </div>
  );
};

export default FileItem;
